"use client";

import { createContext, type ReactNode, use } from "react";

import type { WorkspaceCurrentUser } from "./get-authorization";

/**
 * 認可コンテキストの値。現在ログイン中のワークスペースユーザー情報を保持します。
 */
export type AuthorizationContextValue = {
  currentUser: WorkspaceCurrentUser;
};

const AuthorizationContext = createContext<AuthorizationContextValue | null>(
  null,
);

export type AuthorizationProviderProps = {
  /** 提供したい認可情報（通常は `getWorkspaceAuthorization` の結果） */
  value: AuthorizationContextValue;
  /** 下位ツリーへ共有したい子要素 */
  children: ReactNode;
};

/**
 * 認可情報を React コンテキストとして配信するプロバイダー。
 * Server Component で取得したデータをクライアント側へ橋渡しします。
 */
export function AuthorizationProvider({
  value,
  children,
}: AuthorizationProviderProps) {
  return <AuthorizationContext value={value}>{children}</AuthorizationContext>;
}

/**
 * 認可コンテキストを取得するフック。
 * Provider 外で呼び出された場合は利用ミスとみなし例外を投げます。
 */
export function useAuthorization(): AuthorizationContextValue {
  const context = use(AuthorizationContext);
  if (!context) {
    throw new Error(
      "useAuthorization must be used within an AuthorizationProvider",
    );
  }
  return context;
}
